import { Fragment, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Col,
  Row,
  Pagination,
} from "react-bootstrap";
import {
  getAllData,
  insertInto,
  deleteData,
  updateTableNewWithParams,
} from "../../services/Servicos";
import { getErrorMessage, telefoneMask } from "../../services/helpers";
import ModalErrorPretty from "./ModalErrorPretty";
import ModalSuccessPretty from "./ModalSuccessPretty";
import SearchableSelect from "../common/SearchableSelect";

// Funções de máscara
const cpfMask = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

const cnpjMask = (value) => {
  if (!value) return "";
  return value
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

const getChavePixMask = (tipo, value) => {
  if (!value) return "";
  switch (tipo) {
    case "cpf":
      return cpfMask(value);
    case "cnpj":
      return cnpjMask(value);
    case "telefone":
      return telefoneMask(value);
    default:
      return value;
  }
};

export default function ModalDadosBancarios({
  show,
  setShow,
  title,
  cpfMotorista,
  nomeMotorista = "",
  telefoneMotorista = "",
  emailMotorista = "",
  tipoMotorista = "",
  onConfirmar = () => {},
}) {
  const [loading, setLoading] = useState(false);
  const [dadosBancarios, setDadosBancarios] = useState([]);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState({ show: false, message: "" });
  const [success, setSuccess] = useState({ show: false, message: "" });
  const [bancoCustomizado, setBancoCustomizado] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showConfirmEditModal, setShowConfirmEditModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [camposBloqueados, setCamposBloqueados] = useState(false);

  // Lista dos 20 principais bancos do Brasil
  const bancosPrincipais = [
    { value: "001 - Banco do Brasil", label: "001 - Banco do Brasil" },
    { value: "237 - Bradesco", label: "237 - Bradesco" },
    {
      value: "104 - Caixa Econômica Federal",
      label: "104 - Caixa Econômica Federal",
    },
    { value: "341 - Itaú", label: "341 - Itaú" },
    { value: "033 - Santander", label: "033 - Santander" },
    { value: "212 - Banco Original", label: "212 - Banco Original" },
    {
      value: "260 - Nu Pagamentos (Nubank)",
      label: "260 - Nu Pagamentos (Nubank)",
    },
    { value: "290 - Pagseguro", label: "290 - Pagseguro" },
    { value: "323 - Mercado Pago", label: "323 - Mercado Pago" },
    { value: "077 - Banco Inter", label: "077 - Banco Inter" },
    { value: "748 - Sicredi", label: "748 - Sicredi" },
    { value: "756 - Bancoob (Sicoob)", label: "756 - Bancoob (Sicoob)" },
    { value: "422 - Banco Safra", label: "422 - Banco Safra" },
    { value: "655 - Banco Votorantim", label: "655 - Banco Votorantim" },
    { value: "041 - Banrisul", label: "041 - Banrisul" },
    { value: "389 - Banco Mercantil", label: "389 - Banco Mercantil" },
    {
      value: "070 - BRB - Banco de Brasília",
      label: "070 - BRB - Banco de Brasília",
    },
    { value: "136 - Unicred", label: "136 - Unicred" },
    { value: "380 - PicPay", label: "380 - PicPay" },
    { value: "403 - Cora", label: "403 - Cora" },
    { value: "outro", label: "Outro (digitar manualmente)" },
  ];

  const [formData, setFormData] = useState({
    cpf: cpfMotorista || "",
    name: "",
    telefone: "",
    forma_pagamento: "pix",
    banco: "",
    agencia: "",
    conta: "",
    tipo_conta: "corrente",
    tipo_pix: "cpf",
    chave_pix: "",
  });

  useEffect(() => {
    if (show) {
      carregarDadosBancarios();
    }
  }, [show, cpfMotorista]);

  const carregarDadosBancarios = async () => {
    setLoading(true);
    try {
      const { data } = await getAllData(`dados-bancarios`);
      setDadosBancarios(data || []);

      if (data.find((t) => t.cpf == cpfMotorista)) {
        const dadoEncontrado = data.find((t) => t.cpf == cpfMotorista);
        
        // Detectar automaticamente o tipo de pagamento baseado nos dados preenchidos
        let formaPagamento = "pix"; // padrão
        if (dadoEncontrado.banco.trim()) {
          formaPagamento = "conta";
        } 
        
        setFormData(last => ({
          ...dadoEncontrado,
          forma_pagamento: formaPagamento,
        }));
        
        // Verifica se o banco está na lista ou é customizado
        const bancoExiste = bancosPrincipais.some(
          (b) => b.value === dadoEncontrado.banco,
        );
        setBancoCustomizado(!bancoExiste && dadoEncontrado.banco !== "");
        
        // Bloquear campos do formulário quando encontrar dado cadastrado
        setCamposBloqueados(true);
      } else {
        // Desbloquear campos se não encontrar dado cadastrado
        setCamposBloqueados(false);
        setFormData((prev) => ({
          ...prev,
          cpf: cpfMotorista || "",
          name: nomeMotorista || "",
          telefone: telefoneMotorista || "",
          email: emailMotorista || "",
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar dados bancários:", error);
      // Em caso de erro, ainda pré-preenche com dados do motorista
      setFormData((prev) => ({
        ...prev,
        cpf: cpfMotorista || "",
        name: nomeMotorista || "",
        telefone: telefoneMotorista || "",
        email: emailMotorista || "",
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "banco") {
      if (value === "outro") {
        setBancoCustomizado(true);
        setFormData((prev) => ({ ...prev, [name]: "" }));
      } else {
        setBancoCustomizado(false);
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === "chave_pix") {
      // Aplicar máscara baseada no tipo de PIX
      const maskedValue = getChavePixMask(formData.tipo_pix, value);
      setFormData((prev) => ({ ...prev, [name]: maskedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSalvar = async () => {
    // Confirmar edição
    if (editando) {
      setShowConfirmModal(true);
      return;
    }

    salvarDados();
  };

  const salvarDados = async () => {
    // Validar campos obrigatórios
    if (!formData.cpf || !formData.cpf.trim()) {
      setError({ show: true, message: "CPF é obrigatório!" });
      return;
    }

    if (!formData.name || !formData.name.trim()) {
      setError({ show: true, message: "Nome é obrigatório!" });
      return;
    }

    // Validar baseado na forma de pagamento
    if (formData.forma_pagamento === "pix") {
      if (!formData.tipo_pix || !formData.tipo_pix.trim()) {
        setError({ show: true, message: "Tipo de PIX é obrigatório!" });
        return;
      }
      if (!formData.chave_pix || !formData.chave_pix.trim()) {
        setError({ show: true, message: "Chave PIX é obrigatória!" });
        return;
      }
    } else {
      if (!formData.banco || !formData.banco.trim()) {
        setError({ show: true, message: "Banco é obrigatório!" });
        return;
      }
      if (!formData.agencia || !formData.agencia.trim()) {
        setError({ show: true, message: "Agência é obrigatória!" });
        return;
      }
      if (!formData.conta || !formData.conta.trim()) {
        setError({ show: true, message: "Conta é obrigatória!" });
        return;
      }
    }

    setLoading(true);
    try {
      if (editando) {
        await updateTableNewWithParams(`dados-bancarios`, editando, formData);
        setSuccess({
          show: true,
          message: "Dados bancários atualizados com sucesso!",
        });
      } else {
        await insertInto("dados-bancarios", formData);
        setSuccess({
          show: true,
          message: "Dados bancários cadastrados com sucesso!",
        });
      }
      limparFormulario();
      await carregarDadosBancarios();
    } catch (error) {
      setError({ show: true, message: getErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarEdicao = () => {
    setShowConfirmModal(false);
    salvarDados();
  };

  const handleEditar = (dado) => {
    setItemToEdit(dado);
    setShowConfirmEditModal(true);
  };

  const confirmarEdicao = () => {
    const dado = itemToEdit;
    if (!dado) return;
    
    setShowConfirmEditModal(false);
    setEditando(dado.id);
    
    if(dado?.banco){
      dado.forma_pagamento = "conta"    
    }else{
        dado.forma_pagamento = "pix"    
    }
    setFormData(dado);

    // Verifica se o banco está na lista ou é customizado
    const bancoExiste = bancosPrincipais.some((b) => b.value === dado.banco);
    setBancoCustomizado(!bancoExiste && dado.banco !== "");
    
    // Desbloquear campos para permitir edição
    setCamposBloqueados(false);
    setItemToEdit(null);
  };

  const handleExcluir = (id) => {
    setItemToDelete(id);
    setShowConfirmDeleteModal(true);
  };

  const confirmarExclusao = async () => {
    if (!itemToDelete) return;

    setLoading(true);
    setShowConfirmDeleteModal(false);
    try {
      await deleteData(`dados-bancarios/${itemToDelete}`);
      setSuccess({
        show: true,
        message: "Dados bancários excluídos com sucesso!",
      });
      await carregarDadosBancarios();
      limparFormulario();
    } catch (error) {
      setError({ show: true, message: getErrorMessage(error) });
    } finally {
      setLoading(false);
      setItemToDelete(null);
    }
  };

  const limparFormulario = () => {
    setFormData({
      cpf: cpfMotorista || "",
      name: "",
      telefone: "",
      forma_pagamento: "pix",
      banco: "",
      agencia: "",
      conta: "",
      tipo_conta: "corrente",
      tipo_pix: "cpf",
      chave_pix: "",
    });
    setEditando(null);
    setBancoCustomizado(false);
    setCamposBloqueados(false);
  };

  const handleConfirmarDados = () => {
    // Validar se todos os campos obrigatórios estão preenchidos no formulário
    if (!formData.cpf || !formData.cpf.trim()) {
      setError({ show: true, message: "CPF é obrigatório!" });
      return;
    }
    if (editando) {
      setError({ show: true, message: "Salve os dados editados antes de prosseguir" });
      return;
    }

    if (!formData.name || !formData.name.trim()) {
      setError({ show: true, message: "Nome é obrigatório!" });
      return;
    }

    // Validar baseado na forma de pagamento
    if (formData.forma_pagamento === "pix") {
      if (!formData.tipo_pix || !formData.tipo_pix.trim()) {
        setError({ show: true, message: "Tipo de PIX é obrigatório!" });
        return;
      }
      if (!formData.chave_pix || !formData.chave_pix.trim()) {
        setError({ show: true, message: "Chave PIX é obrigatória!" });
        return;
      }
    } else {
      if (!formData.banco || !formData.banco.trim()) {
        setError({ show: true, message: "Banco é obrigatório!" });
        return;
      }
      if (!formData.agencia || !formData.agencia.trim()) {
        setError({ show: true, message: "Agência é obrigatória!" });
        return;
      }
      if (!formData.conta || !formData.conta.trim()) {
        setError({ show: true, message: "Conta é obrigatória!" });
        return;
      }
    }

    onConfirmar(formData);
    handleClose();
  };

  const handleClose = () => {
    setShow({ show: false });
    limparFormulario();
  };

  return (
    <Fragment>
      <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Fragment>
            <Form>
            <h5>{editando ? "Editar" : "Adicionar"} Dados Bancários</h5>
              {camposBloqueados && !editando && (
                <div className="alert alert-warning mb-3">
                  <i className="bi bi-lock me-2"></i>
                  <strong>Dados bancários já cadastrados!</strong> Para editar ou adicionar novos dados, use os botões na tabela abaixo.
                </div>
              )}
              {tipoMotorista && (
                <div className="alert alert-info mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Tipo de Motorista:</strong> {tipoMotorista}
                </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>CPF</Form.Label>
                    <Form.Control
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      disabled={!!cpfMotorista}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>Nome</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={camposBloqueados}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={emailMotorista}
                      onChange={handleInputChange}
                      disabled={camposBloqueados}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Telefone
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="telefone"
                      maxLength={15}
                      value={telefoneMask(formData.telefone)}
                      onChange={handleInputChange}
                      disabled={camposBloqueados}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Forma de Pagamento
                    </Form.Label>
                    <div className="d-flex gap-3">
                      <div
                        className={`p-3 border rounded flex-fill text-center ${
                          formData.forma_pagamento === "pix"
                            ? "border-primary bg-primary bg-opacity-10"
                            : "border-secondary"
                        } ${camposBloqueados ? "opacity-50" : ""}`}
                        style={{ cursor: camposBloqueados ? "not-allowed" : "pointer", pointerEvents: camposBloqueados ? "none" : "auto" }}
                        onClick={() =>
                          !camposBloqueados && setFormData((prev) => ({
                            ...prev,
                            forma_pagamento: "pix",
                          }))
                        }
                      >
                        <i className="bi bi-qr-code fs-3 d-block mb-2"></i>
                        <strong>PIX</strong>
                      </div>
                      <div
                        className={`p-3 border rounded flex-fill text-center ${
                          formData.forma_pagamento === "conta"
                            ? "border-primary bg-primary bg-opacity-10"
                            : "border-secondary"
                        } ${camposBloqueados ? "opacity-50" : ""}`}
                        style={{ cursor: camposBloqueados ? "not-allowed" : "pointer", pointerEvents: camposBloqueados ? "none" : "auto" }}
                        onClick={() =>
                          !camposBloqueados && setFormData((prev) => ({
                            ...prev,
                            forma_pagamento: "conta",
                          }))
                        }
                      >
                        <i className="bi bi-bank fs-3 d-block mb-2"></i>
                        <strong>Transferência Bancária</strong>
                      </div>
                    </div>
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-5">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Banco
                      {bancoCustomizado && (
                        <i
                          className="bi bi-backspace ms-2"
                          style={{ cursor: "pointer", color: "#dc3545" }}
                          onClick={() => {
                            setBancoCustomizado(false);
                            setFormData((prev) => ({ ...prev, banco: "" }));
                          }}
                          title="Voltar para lista de bancos"
                        ></i>
                      )}
                    </Form.Label>
                    {!bancoCustomizado ? (
                      <SearchableSelect
                        name="banco"
                        value={formData.banco}
                        onChange={handleInputChange}
                        options={bancosPrincipais}
                        placeholder="Selecione o banco"
                        searchPlaceholder="Buscar banco..."
                        disabled={formData.forma_pagamento === "pix" || camposBloqueados}
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        name="banco"
                        value={formData.banco}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            banco: e.target.value,
                          }))
                        }
                        placeholder="Digite o código ou nome do banco"
                        disabled={formData.forma_pagamento === "pix" || camposBloqueados}
                        required
                      />
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Agência
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="agencia"
                      value={formData.agencia}
                      onChange={handleInputChange}
                      disabled={formData.forma_pagamento === "pix" || camposBloqueados}
                      required
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Conta
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="conta"
                      value={formData.conta}
                      onChange={handleInputChange}
                      disabled={formData.forma_pagamento === "pix" || camposBloqueados}
                      required
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-5">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Tipo de Conta
                    </Form.Label>
                    <SearchableSelect
                      name="tipo_conta"
                      value={formData.tipo_conta}
                      onChange={handleInputChange}
                      options={[
                        { value: "corrente", label: "Corrente" },
                        { value: "poupanca", label: "Poupança" },
                      ]}
                      placeholder="Selecione o tipo de conta"
                      disabled={formData.forma_pagamento === "pix" || camposBloqueados}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Tipo de PIX
                    </Form.Label>
                    <SearchableSelect
                      name="tipo_pix"
                      value={formData.tipo_pix}
                      onChange={handleInputChange}
                      options={[
                        { value: "cpf", label: "CPF" },
                        { value: "cnpj", label: "CNPJ" },
                        { value: "email", label: "Email" },
                        { value: "telefone", label: "Telefone" },
                        {
                          value: "chave_aleatoria",
                          label: "Chave Aleatória",
                        },
                      ]}
                      placeholder="Selecione o tipo de PIX"
                      disabled={formData.forma_pagamento === "conta" || camposBloqueados}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: "bold" }}>
                      Chave PIX
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="chave_pix"
                      value={getChavePixMask(formData.tipo_pix, formData.chave_pix)}
                      onChange={handleInputChange}
                      disabled={formData.forma_pagamento === "conta" || camposBloqueados}
                      maxLength={
                        formData.tipo_pix === "cpf" ? 14 :
                        formData.tipo_pix === "cnpj" ? 18 :
                        formData.tipo_pix === "telefone" ? 15 : undefined
                      }
                      placeholder={
                        formData.tipo_pix === "cpf" ? "000.000.000-00" :
                        formData.tipo_pix === "cnpj" ? "00.000.000/0000-00" :
                        formData.tipo_pix === "telefone" ? "(00) 00000-0000" :
                        formData.tipo_pix === "email" ? "exemplo@email.com" :
                        "Digite a chave PIX"
                      }
                    />
                  </Form.Group>
                </div>
              </div>
            </Form>

            {dadosBancarios.length > 0 &&
              (() => {
                // Filtrar dados baseado no termo de busca
                const filteredData = dadosBancarios.filter((dado) => {
                  const searchLower = searchTerm.toLowerCase();
                  return (
                    dado.name.toLowerCase().includes(searchLower) ||
                    dado.cpf.includes(searchTerm)
                  );
                });

                // Lógica de paginação
                const indexOfLastItem = currentPage * itemsPerPage;
                const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                const currentItems = filteredData.slice(
                  indexOfFirstItem,
                  indexOfLastItem,
                );
                const totalPages = Math.ceil(
                  filteredData.length / itemsPerPage,
                );

                return (
                  <Fragment>
                    <hr className="my-4" />
                    <h5>
                      Dados Cadastrados ({dadosBancarios.length}{" "}
                      {dadosBancarios.length === 1 ? "registro" : "registros"})
                    </h5>
                    <div className=" responsive-table" style={{position: 'relative',height:'400px'}}>
                        <Table striped bordered hover>
                        <thead>
                            <tr>
                            <th>Nome</th>
                            <th>CPF</th>
                            <th>Banco</th>
                            <th>Agência</th>
                            <th>Conta</th>
                            <th>Tipo</th>
                            <th>PIX</th>
                            <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody >
                            {currentItems.map((dado) => (
                            <tr key={dado.id} className={formData.cpf == dado.cpf ? "bg-selected" : ""} onClick={() => setFormData(last => ({...last, ...dado}))}>
                                <td>{dado.name}</td>
                                <td style={{ whiteSpace: "nowrap" }}>{dado.cpf}</td>
                                <td>{dado.banco}</td>
                                <td>{dado.agencia}</td>
                                <td>{dado.conta}</td>
                                <td>{dado.tipo_conta}</td>
                                <td>{dado.chave_pix}</td>
                                <td>
                                <div className="admin">
                                    <span className="editar">
                                    <i
                                        className="bi bi-pencil"
                                        title="Editar"
                                        onClick={() => handleEditar(dado)}
                                    ></i>
                                    </span>
                                    <span className="excluir">
                                    <i
                                        className="bi bi-trash"
                                        title="Excluir"
                                        onClick={() => handleExcluir(dado.id)}
                                    ></i>
                                    </span>
                                </div>
                                </td>
                            </tr>
                            ))}
                        </tbody>
                        </Table>
                    </div>
                
                    <hr />
                    <Row>
                      <Col>
                        <div className="mb-3">
                          <Form.Group>
                            <Form.Control
                              type="text"
                              placeholder="Buscar por nome ou CPF..."
                              value={searchTerm}
                              style={{ borderColor: "#0000009f" }}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1); // Reset para primeira página ao buscar
                              }}
                            />
                          </Form.Group>
                          {searchTerm && (
                            <small className="text-muted">
                              {filteredData.length}{" "}
                              {filteredData.length === 1
                                ? "resultado encontrado"
                                : "resultados encontrados"}
                            </small>
                          )}
                        </div>
                      </Col>
                      <Col>
                        <div className="d-flex justify-content-end">
                          <Pagination>
                            <Pagination.First
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                            />
                            <Pagination.Prev
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(1, prev - 1))
                              }
                              disabled={currentPage === 1}
                            />

                            {[...Array(totalPages)].map((_, index) => {
                              const pageNumber = index + 1;
                              // Mostrar apenas algumas páginas próximas à atual
                              if (
                                pageNumber === 1 ||
                                pageNumber === totalPages ||
                                (pageNumber >= currentPage - 1 &&
                                  pageNumber <= currentPage + 1)
                              ) {
                                return (
                                  <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => setCurrentPage(pageNumber)}
                                  >
                                    {pageNumber}
                                  </Pagination.Item>
                                );
                              } else if (
                                pageNumber === currentPage - 2 ||
                                pageNumber === currentPage + 2
                              ) {
                                return (
                                  <Pagination.Ellipsis
                                    key={pageNumber}
                                    disabled
                                  />
                                );
                              }
                              return null;
                            })}

                            <Pagination.Next
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(totalPages, prev + 1),
                                )
                              }
                              disabled={currentPage === totalPages}
                            />
                            <Pagination.Last
                              onClick={() => setCurrentPage(totalPages)}
                              disabled={currentPage === totalPages}
                            />
                          </Pagination>
                        </div>
                      </Col>
                    </Row>
                  </Fragment>
                );
              })()}
          </Fragment>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col sm="4" className="mb-2">
              <Button
                variant="primary"
                onClick={handleSalvar}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : "Salvar dados bancários"}
              </Button>
            </Col>
            <Col sm="4" className="mb-2">
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
            </Col>
            <Col sm="4" className="mb-2">
              <Button
                variant="success"
                onClick={handleConfirmarDados}
                disabled={dadosBancarios.length === 0}
              >
                Confirmar e Continuar
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        style={{ backgroundColor: "#000000a4" }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            Confirmar Edição
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Você está editando um registro existente.</p>
          <p>
            <strong>Deseja realmente salvar as alterações?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button variant="primary" onClick={handleConfirmarEdicao}>
                Sim, Salvar Alterações
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        centered
        style={{ backgroundColor: "#000000a4" }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle text-danger me-2"></i>
            Confirmar Exclusão
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Você está prestes a excluir este registro permanentemente.</p>
          <p>
            <strong>Deseja realmente continuar?</strong>
          </p>
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfirmDeleteModal(false);
                  setItemToDelete(null);
                }}
              >
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button variant="danger" onClick={confirmarExclusao}>
                Sim, Excluir
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação de Edição */}
      <Modal
        show={showConfirmEditModal}
        onHide={() => {
          setShowConfirmEditModal(false);
          setItemToEdit(null);
        }}
        centered
        style={{ backgroundColor: "#000000a4" }}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-circle text-info me-2"></i>
            Confirmar Edição
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemToEdit && (
            <>
              <p>Você está prestes a editar os dados bancários de:</p>
              <div className="alert alert-info mb-0">
                <p className="mb-1"><strong>Nome:</strong> {itemToEdit.name}</p>
                <p className="mb-1"><strong>CPF:</strong> {itemToEdit.cpf}</p>
                {itemToEdit.banco && (
                  <p className="mb-1"><strong>Banco:</strong> {itemToEdit.banco}</p>
                )}
                {itemToEdit.chave_pix && (
                  <p className="mb-0"><strong>Chave PIX:</strong> {itemToEdit.chave_pix}</p>
                )}
              </div>
              <p className="mt-3">
                <strong>Deseja realmente editar este registro?</strong>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="d-block">
          <Row>
            <Col>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfirmEditModal(false);
                  setItemToEdit(null);
                }}
              >
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button variant="primary" onClick={confirmarEdicao}>
                Sim, Editar
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {error.show && <ModalErrorPretty setShow={setError} {...error} />}
      {success.show && <ModalSuccessPretty setShow={setSuccess} {...success} />}
    </Fragment>
  );
}
